/**
 * MD-29 Content Management - admin content fetch hook.
 *
 * Pattern reuse tu use-admin-agents.ts (extractErrorMessage envelope-safe).
 * Endpoint base: /api/v1/admin/content/*
 *
 * Spec: specs/domains/admin/F-A-CONTENT-MGMT.md
 */

import { useEffect, useState, useCallback } from 'react';

const API_BASE = '/api/v1/admin/content';

export interface UseContentState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

function extractErrorMessage(body: unknown, status: number): string {
  if (!body || typeof body !== 'object') return `HTTP ${status}`;
  const obj = body as Record<string, unknown>;
  const err = obj.error;
  if (typeof err === 'string' && err.trim()) return err;
  if (err && typeof err === 'object') {
    const inner = err as Record<string, unknown>;
    if (typeof inner.message === 'string' && inner.message.trim()) return inner.message;
    if (typeof inner.code === 'string') return inner.code;
  }
  if (typeof obj.message === 'string' && obj.message.trim()) return obj.message;
  return `HTTP ${status}`;
}

function toErrorString(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === 'string') return e;
  try {
    return JSON.stringify(e);
  } catch {
    return 'Loi khong xac dinh';
  }
}

function getAuthHeader(): Record<string, string> {
  try {
    const token = localStorage.getItem('asklaw_access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export function useAdminContent<T>(path: string): UseContentState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}${path}`, { headers: getAuthHeader() });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(extractErrorMessage(body, res.status));
      }
      const json = (await res.json()) as T;
      setData(json);
    } catch (e) {
      setError(toErrorString(e));
    } finally {
      setLoading(false);
    }
  }, [path]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

// ---------- Type ----------

export interface BlogItem {
  file: string;
  slug: string;
  title: string;
  cluster: string;
  lang: string;
  audience: string;
  status: string;
  author: string;
  publish_date: string | null;
  updated_date: string | null;
  citations_count: number;
  hreflang_en: string | null;
  og_image: string | null;
  primary_keyword: string | null;
  body_word_count: number;
  disclaimer_present: boolean;
  disclaimer_marker_hits: number;
  upl_warning: boolean;
  upl_hits: Array<{ id: string; severity: string; count: number }>;
  /** Patch 2026-05-12 MD-29 (DECISIONS s2026-05-12-MD29-PATCH-BLOG-SOCIAL-COL).
   *  Channel attribution tu runtime D1 join. Phase 0 chi co channel 'facebook'. */
  promoted_channels?: PromotedChannel[];
}

export interface PromotedChannel {
  channel: 'facebook';
  status: 'published' | 'scheduled';
  posted_at: string | null;
  scheduled_at: string | null;
  post_url: string | null;
  post_id: string;
}

export interface BlogListResponse {
  total: number;
  total_all: number;
  generated_at: string;
  content_hash: string;
  summary: {
    total: number;
    by_cluster: Record<string, number>;
    by_lang: Record<string, number>;
    by_status: Record<string, number>;
    by_audience: Record<string, number>;
    upl_warning_count: number;
    disclaimer_present_count: number;
  };
  items: BlogItem[];
  _promoted_meta?: {
    total_fb_rows: number;
    orphan_fb_count: number;
    served_from_cache: boolean;
    cache_ttl_sec: number;
  } | null;
}

export interface ChannelRow {
  id: string;
  label: string;
  status: 'live' | 'planned';
  module: string;
}

export interface ChannelsResponse {
  channels: ChannelRow[];
}

// ---------- Social FB (reuse MD-26 /api/v1/admin/social/posts) ----------

export interface FbPost {
  fb_post_id: string;
  fb_post_url: string;
  posted_at: number;
  caption_snippet: string;
  content_category: string | null;
  topic_tag: string | null;
  reach: number;
  impressions?: number;
  reactions?: number;
  comments?: number;
  shares?: number;
  link_clicks?: number;
  engagement_rate?: number;
  ctr?: number | null;
}

export interface FbPostsResponse {
  posts: FbPost[];
  total: number;
  has_more: boolean;
}

const SOCIAL_API_BASE = '/api/v1/admin/social';

export function useFbPosts(query: string): UseContentState<FbPostsResponse> {
  const [data, setData] = useState<FbPostsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${SOCIAL_API_BASE}/posts${query}`, { headers: getAuthHeader() });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(extractErrorMessage(body, res.status));
      }
      const json = (await res.json()) as FbPostsResponse;
      setData(json);
    } catch (e) {
      setError(toErrorString(e));
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}
